/**
 * The ticked rows of a studio's order list. One hook for both lists (business cards, logo
 * files), which differ in every column and agree on this: a row has an id, and the server
 * says whether it may go.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { keepPicked, type PickableRow } from "./lib";

export interface OrderPicks {
  size: number;
  picked: ReadonlySet<string>;
  /** The ticked ids, in the order they were ticked. */
  ids: string[];
  has: (id: string) => boolean;
  toggle: (id: string) => void;
  /** The box over the column: every row of the page that may go, at once. */
  setMany: (ids: readonly string[], on: boolean) => void;
  clear: () => void;
}

export function useOrderPicks(rows: readonly PickableRow[]): OrderPicks {
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set());

  // A new page, a new search or a reload keeps only the ticks of rows that are still there
  useEffect(() => {
    setPicked((prev) => keepPicked(prev, rows));
  }, [rows]);

  const toggle = useCallback((id: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setMany = useCallback((ids: readonly string[], on: boolean) => {
    setPicked((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  const clear = useCallback(() => setPicked(new Set()), []);
  const has = useCallback((id: string) => picked.has(id), [picked]);
  const ids = useMemo(() => [...picked], [picked]);

  return useMemo(() => ({ size: picked.size, picked, ids, has, toggle, setMany, clear }), [picked, ids, has, toggle, setMany, clear]);
}
